import { useState, useEffect, useMemo } from 'react';
import { C, css } from './Dashboard';
import { CHROME_STORE_URL } from './links';

// The "Getting started" checklist for a new workspace's admin.
//
// Progress is DERIVED from the data the dashboard already holds (clients, time entries,
// invoices, team, the org's own settings), never stored, so it can't drift out of date.
// The two things the app can't observe (reviewing billing defaults, installing the
// extension somewhere other than this browser) are ticked by hand and remembered in this
// browser. The panel opens by itself once, on first arrival from the sign-up wizard; after
// that it opens from the sidebar pill, and closing it just hides it. Nothing here blocks.

const visuallyHidden = { position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(0 0 0 0)', whiteSpace:'nowrap' };
const NEW_WORKSPACE_DAYS = 45; // how long the pill stays in the sidebar for a workspace that never opened the panel

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch { return {}; }
}

export function useGettingStarted({ org, profile, isAdmin, viewAs, isIndividual, clients, myEntries, invoices, orgMembers, pendingInvites, extInstalled }) {
  const orgId = org?.id;
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState({});
  const [seen, setSeen] = useState(false);

  // Per-workspace, per-browser memory.
  useEffect(() => {
    if (!orgId) return;
    setManual(readJson(`tk_gs_manual_${orgId}`));
    try { setSeen(localStorage.getItem(`tk_gs_seen_${orgId}`) === '1'); } catch { setSeen(false); }
  }, [orgId]);

  // First arrival from the sign-up wizard: open the panel once. This runs after the
  // page's own effects, which is what lets the page tour see the flag first and stand aside.
  useEffect(() => {
    if (!orgId) return;
    let pending = null;
    try { pending = localStorage.getItem('tk_gs_pending'); } catch { /* storage blocked */ }
    if (pending === orgId) {
      try { localStorage.removeItem('tk_gs_pending'); localStorage.setItem(`tk_gs_seen_${orgId}`, '1'); } catch { /* ignore */ }
      setSeen(true);
      setOpen(true);
    }
  }, [orgId]);

  const setManualDone = (id, value) => {
    setManual(prev => {
      const next = { ...prev, [id]: value };
      try { localStorage.setItem(`tk_gs_manual_${orgId}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const enabled = !!orgId && isAdmin && !viewAs;

  const items = useMemo(() => {
    if (!enabled) return [];
    const others = (orgMembers || []).filter(m => m.user_id !== profile?.id && !m.ended_at).length;
    const list = [
      { id: 'client', title: 'Add your first client',
        desc: 'The people or companies you bill. Have a list already? Import it from a spreadsheet.',
        done: (clients || []).some(c => c.is_active !== false), go: { kind: 'nav', to: 'clients' }, cta: 'Add client' },
      { id: 'billing', title: 'Check your billing and invoice settings',
        desc: 'Billing increment, payment terms and invoice numbering start at sensible defaults. Look them over and change what you need.',
        manual: true, done: !!manual.billing, go: { kind: 'settings', section: 'Billing' }, cta: 'Open settings' },
      { id: 'brand', title: 'Add your address and logo',
        desc: 'They appear on every invoice, so clients can see who it is from.',
        done: !!org?.address_line1, go: { kind: 'settings', section: 'Organization' }, cta: 'Open settings' },
      { id: 'time', title: 'Log your first time entry',
        desc: 'Start the timer, or add hours you already worked.',
        done: (myEntries || []).length > 0, go: { kind: 'nav', to: 'timer' }, cta: 'Open Timer' },
    ];
    if (!isIndividual) {
      list.push({ id: 'team', title: 'Invite your team',
        desc: 'Contractors, employees and other admins each get their own login, and their time lands here.',
        done: others > 0 || (pendingInvites || []).length > 0, go: { kind: 'nav', to: 'team' }, cta: 'Open Team' });
    }
    list.push(
      { id: 'invoice', title: 'Create your first invoice',
        desc: 'Built from the time you logged. You choose when it goes out.',
        done: (invoices || []).length > 0, go: { kind: 'nav', to: 'invoices' }, cta: 'Open Invoices' },
      { id: 'payments', title: 'Let clients pay online',
        desc: 'Connect Stripe, or add a payment link, so invoices have a Pay Now button.',
        done: !!(org?.stripe_enabled || org?.invoice_payment_link), go: { kind: 'settings', section: 'Integrations' }, cta: 'Open settings' },
      { id: 'ext', title: 'Install the Chrome extension',
        desc: 'Start and stop timers from Gmail, Google Calendar, Asana and more, without switching tabs.',
        manual: true, autoDone: !!extInstalled, done: !!(extInstalled || manual.ext), go: { kind: 'link', href: CHROME_STORE_URL }, cta: 'Get the extension' },
    );
    return list;
  }, [enabled, org?.address_line1, org?.stripe_enabled, org?.invoice_payment_link, isIndividual, clients, myEntries, invoices, orgMembers, pendingInvites, extInstalled, manual, profile?.id]);

  const done = items.filter(i => i.done).length;
  const total = items.length;

  // The pill shows for a new workspace, or one that has already used the guide. Older,
  // established workspaces never see it unprompted.
  const ageDays = org?.created_at ? (Date.now() - new Date(org.created_at).getTime()) / 86400000 : Infinity;
  const visible = enabled && (seen || ageDays <= NEW_WORKSPACE_DAYS);

  return { enabled, visible, open, setOpen, items, done, total, manual, setManualDone };
}

// ─── Sidebar pill ────────────────────────────────────────────────────────────
export function GettingStartedPill({ gs }) {
  if (!gs.visible) return null;
  const complete = gs.total > 0 && gs.done === gs.total;
  const pct = gs.total ? Math.round((gs.done / gs.total) * 100) : 0;
  return (
    <button type="button" data-tour="setup-guide" onClick={() => gs.setOpen(o => !o)}
      aria-expanded={gs.open} aria-label={`Getting started checklist, ${gs.done} of ${gs.total} done`}
      title="Your setup checklist. Close it any time; open it again from here."
      style={{ display:'block', width:'100%', textAlign:'left', fontFamily:'inherit', cursor:'pointer', marginBottom:'10px',
        padding:'8px 10px', borderRadius:'8px',
        border:`1px solid ${complete ? C.border : C.accent}`,
        background: gs.open ? `${C.accent}22` : (complete ? 'transparent' : `${C.accent}12`) }}>
      <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' }}>
        <span style={{ fontSize:'12px', fontWeight:700, color: complete ? C.muted : C.accent }}>{complete ? '✓ Setup guide' : 'Getting started'}</span>
        <span style={{ fontSize:'11px', color:C.muted }}>{gs.done}/{gs.total}</span>
      </span>
      <span style={{ display:'block', height:'3px', borderRadius:'2px', background:C.border, marginTop:'6px', overflow:'hidden' }}>
        <span style={{ display:'block', height:'100%', width:`${pct}%`, background:C.accent, transition:'width 0.3s' }} />
      </span>
    </button>
  );
}

// ─── The checklist itself ────────────────────────────────────────────────────
export function GettingStartedPanel({ gs, onGo }) {
  // One row open at a time. Until the person picks one, the next thing to do is open.
  const [openId, setOpenId] = useState(null);
  const firstTodo = gs.items.find(i => !i.done);
  const expanded = openId === null ? firstTodo?.id : openId;
  useEffect(() => {
    if (!gs.open) return;
    const esc = (e) => { if (e.key === 'Escape') gs.setOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [gs.open]); // eslint-disable-line

  if (!gs.visible || !gs.open) return null;
  const complete = gs.total > 0 && gs.done === gs.total;
  const pct = gs.total ? Math.round((gs.done / gs.total) * 100) : 0;
  const link = { background:'none', border:'none', padding:0, cursor:'pointer', color:C.muted, fontSize:'11px', fontFamily:'inherit', textDecoration:'underline' };

  return (
    <div role="dialog" aria-label="Getting started"
      style={{ position:'fixed', bottom:'16px', left:'232px', width:'380px', maxWidth:'calc(100vw - 248px)', maxHeight:'calc(100vh - 32px)',
        background:C.surface, border:`1px solid ${C.border}`, borderRadius:'12px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)',
        display:'flex', flexDirection:'column', zIndex:1000, overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 12px', borderBottom:`1px solid ${C.border}`, background:C.bg }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:700, fontSize:'14px', color:C.text }}>Getting started</div>
          <button type="button" onClick={() => gs.setOpen(false)} aria-label="Hide the checklist" title="Hide. Open it again from the sidebar."
            style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:'18px', lineHeight:1 }}>×</button>
        </div>
        <div style={{ fontSize:'12px', color:C.muted, marginTop:'4px' }}>
          {complete ? 'Everything is set up.' : `${gs.done} of ${gs.total} done. Do them in any order, or come back later.`}
        </div>
        <div style={{ height:'4px', borderRadius:'2px', background:C.border, marginTop:'10px', overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:C.accent, transition:'width 0.3s' }} />
        </div>
      </div>

      <div style={{ overflowY:'auto', padding:'4px 0' }}>
        {gs.items.map(it => {
          const isOpen = expanded === it.id;
          return (
            <div key={it.id} style={{ borderBottom:`1px solid ${C.border}33` }}>
              <button type="button" onClick={() => setOpenId(isOpen ? '' : it.id)} aria-expanded={isOpen}
                style={{ display:'flex', alignItems:'center', gap:'12px', width:'100%', textAlign:'left', background:'none', border:'none', cursor:'pointer', padding:'10px 16px', fontFamily:'inherit' }}>
                <span aria-hidden="true" style={{ width:'20px', height:'20px', borderRadius:'50%', flexShrink:0,
                  border:`2px solid ${it.done ? C.accent : C.muted}`, background: it.done ? C.accent : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center', color:C.bg, fontSize:'12px', fontWeight:800 }}>
                  {it.done ? '✓' : ''}
                </span>
                <span style={{ flex:1, fontSize:'13px', fontWeight:700, color: it.done ? C.muted : C.textBright, textDecoration: it.done ? 'line-through' : 'none' }}>
                  <span style={visuallyHidden}>{it.done ? 'Done: ' : 'To do: '}</span>{it.title}
                </span>
                <span aria-hidden="true" style={{ color:C.muted, fontSize:'11px' }}>{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div style={{ padding:'0 16px 12px 48px' }}>
                  {!it.done && <div style={{ fontSize:'12px', color:C.muted, lineHeight:1.45 }}>{it.desc}</div>}
                  <div style={{ display:'flex', alignItems:'center', gap:'12px', marginTop: it.done ? 0 : '8px' }}>
                    <button type="button" onClick={() => onGo(it.go)}
                      style={{ ...css.btn(it.done ? 'secondary' : 'primary'), padding:'5px 12px', fontSize:'12px' }}>
                      {it.done ? 'Open' : it.cta}
                    </button>
                    {it.manual && !it.autoDone && (
                      <button type="button" style={link} onClick={() => gs.setManualDone(it.id, !gs.manual[it.id])}
                        title={it.id === 'ext'
                          ? 'TimeKeep can only tell the extension is installed in this browser, so tick this yourself if you installed it somewhere else.'
                          : 'TimeKeep cannot tell whether you have looked these over, so tick it yourself.'}>
                        {gs.manual[it.id] ? 'Undo' : 'Mark done'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ padding:'10px 16px', borderTop:`1px solid ${C.border}`, background:C.bg, fontSize:'11px', color:C.muted, lineHeight:1.5 }}>
        You can hide this and bring it back any time from <strong style={{ color:C.text }}>Getting started</strong> in the sidebar.
      </div>
    </div>
  );
}
